import React from "react";
import { useTranslation } from "react-i18next";
import { getMessagePlainText, renderMessageContent } from "./markdown";
import type { WidgetMessage } from "./types";

const USER_ROLE = "user";
const COPY_TRANSLATION_KEY = "chatbot_copy";
const COPIED_TRANSLATION_KEY = "chatbot_copied";
const NAMED_CONTEXT = "named";
const BUTTON_TYPE = "button";
const COPIED_CLASS_NAME = " copied";
const ASSISTANT_ICON_CLASS_NAME = "ti ti-sparkles";
const COPIED_ICON_CLASS_NAME = "ti ti-check";
const COPY_ICON_CLASS_NAME = "ti ti-copy";

const areReferenceImagesEqual = (
  previousImages: WidgetMessage["referenceImages"],
  nextImages: WidgetMessage["referenceImages"]
): boolean => {
  const previous = previousImages ?? [];
  const next = nextImages ?? [];
  if (previous.length !== next.length) return false;

  return previous.every((image, index) => (
    image.image_url === next[index]?.image_url && image.file_name === next[index]?.file_name
  ));
};

interface MessageBubbleProps {
  message: WidgetMessage;
  copiedId: string | null;
  welcomeName: string | null;
  highlightedTurnId?: string | null;
  onCopy: (id: string, text: string) => void;
  onOpenImageViewer?: (src: string, alt: string, filename?: string) => void;
  onCopyImage?: (src: string) => Promise<void>;
  onDownloadImage?: (src: string, filename?: string) => Promise<boolean>;
  getImageStarState?: (message: WidgetMessage, src: string, alt: string) => {
    canStar: boolean;
    isStarred: boolean;
    onToggleStar?: () => void | Promise<void>;
  };
  getImageLineState?: (message: WidgetMessage, src: string, alt: string) => {
    canSend: boolean;
    onSend?: () => void | Promise<void>;
  };
}

const MessageBubbleComponent = ({
  message,
  copiedId,
  welcomeName,
  highlightedTurnId,
  onCopy,
  onOpenImageViewer,
  onCopyImage,
  onDownloadImage,
  getImageStarState,
  getImageLineState,
}: MessageBubbleProps): React.ReactElement | null => {
  const { t } = useTranslation();
  const translatedContent = message.translationKey
    ? t(message.translationKey, welcomeName ? { context: NAMED_CONTEXT, name: welcomeName } : {})
    : "";
  const copyText = message.content || translatedContent;

  const showCopy = !!copyText && (!message.localOnly || !!message.translationKey);
  const isMessageCopied = copiedId === message.id;
  const copyLabel = t(COPY_TRANSLATION_KEY);
  const copiedLabel = t(COPIED_TRANSLATION_KEY);
  const referenceImages = message.referenceImages ?? [];
  const isUserMessage = message.role === USER_ROLE;

  if (!message.translationKey && !message.content && referenceImages.length === 0) {
    return null;
  }

  const renderContent = (): React.ReactNode => {
    if (message.translationKey) {
      return translatedContent;
    }
    if (message.content) {
      return renderMessageContent(
        message.content,
        showCopy
          ? {
              onCopyTable: onCopy,
              onOpenImageViewer,
              onCopyImage,
              onDownloadImage,
              getImageStarState: (src, alt) => getImageStarState?.(message, src, alt) ?? {
                canStar: false,
                isStarred: false,
              },
              getImageLineState: (src, alt) => getImageLineState?.(message, src, alt) ?? {
                canSend: false,
              },
              messageId: message.id,
              copiedId,
              copyLabel,
              copiedLabel,
            }
          : {
              onOpenImageViewer,
              onCopyImage,
              onDownloadImage,
              getImageStarState: (src, alt) => getImageStarState?.(message, src, alt) ?? {
                canStar: false,
                isStarred: false,
              },
              getImageLineState: (src, alt) => getImageLineState?.(message, src, alt) ?? {
                canSend: false,
              },
            }
      );
    }
    return null;
  };

  return (
    <div
      className={`restaurant-chatbot-message-row ${message.role}${showCopy ? " has-copy" : ""}${isMessageCopied ? " is-copied" : ""}${highlightedTurnId && highlightedTurnId === message.turnId ? " is-highlighted" : ""}`}
      data-turn-id={message.turnId ?? ""}
      data-role={message.role}
    >
      {!isUserMessage && (
        <div className="restaurant-chatbot-avatar" aria-hidden="true">
          <i className={ASSISTANT_ICON_CLASS_NAME} />
        </div>
      )}
      <div className="restaurant-chatbot-message-group">
        <div className="restaurant-chatbot-message">
          {renderContent()}
          {referenceImages.length > 0 && (
            <div className="restaurant-chatbot-message-reference-images">
              {referenceImages.map((image) => (
                <button
                   className="restaurant-chatbot-message-reference-image-button"
                   type="button"
                   key={image.image_url}
                   onClick={() => onOpenImageViewer?.(image.image_url, image.file_name, image.file_name)}
                   title={image.file_name}
                >
                  <img src={image.image_url} alt={image.file_name} loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>
        {showCopy && (
          <div className="restaurant-chatbot-message-actions">
            <button
              className={`restaurant-chatbot-copy-btn${isMessageCopied ? COPIED_CLASS_NAME : ""}`}
              type={BUTTON_TYPE}
              onClick={() => onCopy(message.id, getMessagePlainText(copyText))}
              title={isMessageCopied ? copiedLabel : copyLabel}
            >
              <i className={isMessageCopied ? COPIED_ICON_CLASS_NAME : COPY_ICON_CLASS_NAME} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export const MessageBubble = React.memo(MessageBubbleComponent, (prevProps, nextProps) => {
  return (
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.translationKey === nextProps.message.translationKey &&
    areReferenceImagesEqual(prevProps.message.referenceImages, nextProps.message.referenceImages) &&
    prevProps.copiedId === nextProps.copiedId &&
    prevProps.highlightedTurnId === nextProps.highlightedTurnId &&
    prevProps.welcomeName === nextProps.welcomeName &&
    prevProps.getImageStarState === nextProps.getImageStarState &&
    prevProps.getImageLineState === nextProps.getImageLineState
  );
});
